import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ForestOutlinedIcon from '@mui/icons-material/ForestOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import TagIcon from '@mui/icons-material/Tag';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import {type FC, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import AutocompleteEditableOnHover from '@/components/common/forms/AutocompleteEditableOnHover';
import CalendarPickerEditableOnHover from '@/components/common/forms/CalendarPickerEditableOnHover';
import InputTextEditableOnHover from '@/components/common/forms/InputTextEditableOnHover';
import type {Option} from '@/components/common/forms/MultiSelect';
import MultiSelectEditableOnHover from '@/components/common/forms/MultiSelectEditableOnHover';
import SelectEditableOnHover from '@/components/common/forms/SelectEditableOnHover';
import LabelChip from '@/components/common/LabelChip';
import Title from '@/components/common/Title';
import {COUNCILS} from '@/config';
import type {Appraiser} from '@/domain/entities/Appraiser';
import type {ForestMass} from '@/domain/entities/ForestMass';
import type {Species} from '@/domain/entities/Species';
import type UserPermissions from '@/domain/useCases/userPermissions';

//TYPES
type Props = {
  permissions: UserPermissions;
  forestMass: ForestMass;
  appraisers: Array<Appraiser>;
  species: Array<Species>;
  selectedSpecies: Array<Species>;
  onForestMassChange: (value: ForestMass) => void;
  onSelectedSpeciesChange: (uuids: Array<string>) => void;
};

const ForestMassGeneralInfo: FC<Props> = ({
  permissions,
  forestMass,
  appraisers,
  species,
  selectedSpecies,
  onForestMassChange,
  onSelectedSpeciesChange,
}) => {
  const {t} = useTranslation();

  const canEdit = permissions.hasPermissionToEditForestMass();

  const selectedConcello = useMemo(() => COUNCILS?.find((council) => council === forestMass.concello), [forestMass]);

  const selectedAppraiser = useMemo(
    () => appraisers?.find((a) => a.uuid === forestMass.appraiserUuid),
    [forestMass, appraisers],
  );

  const speciesOptions = useMemo(() => speciesToOptions(species), [species]);
  const selectedSpeciesOptions = useMemo(() => speciesToOptions(selectedSpecies), [selectedSpecies]);

  const update = (newValue?: Partial<ForestMass>) =>
    onForestMassChange({
      ...forestMass,
      ...newValue,
    });

  const handleSelectedSpeciesChange = (options: Array<Option>) => {
    onSelectedSpeciesChange(options.map((op) => op.id));
  };

  return (
    <>
      <Title size="h6" label={t('forestMass.info')} subtitleLabel={canEdit ? t('forestMass.subInfo') : ''} />
      <LabelChip value={t('forestMass.concello')} icon={LocationOnOutlinedIcon}>
        <AutocompleteEditableOnHover
          editable={canEdit}
          options={COUNCILS}
          value={selectedConcello}
          onValueChange={(concello) => update({concello})}
        />
      </LabelChip>

      <Divider sx={{m: 2}} variant="fullWidth" />

      <LabelChip value={t('forestMass.code')} icon={TagIcon}>
        <InputTextEditableOnHover
          editable={canEdit}
          value={forestMass.code}
          onSave={(code) => code && update({code})}
        />
      </LabelChip>

      <Divider sx={{m: 2}} variant="fullWidth" />

      <LabelChip value={t('forestMass.plannedCuttingDate')} icon={CalendarTodayOutlinedIcon}>
        <CalendarPickerEditableOnHover
          editable={canEdit}
          value={forestMass.plannedCuttingDate || dayjs()}
          onChange={(plannedCuttingDate) => plannedCuttingDate && update({plannedCuttingDate})}
        />
      </LabelChip>

      <Divider sx={{m: 2}} variant="fullWidth" />

      <LabelChip value={t('forestMass.appraiser')} icon={PersonSearchOutlinedIcon}>
        <Stack direction="column" spacing={1} sx={{flex: 1}}>
          <SelectEditableOnHover
            editable={canEdit}
            options={appraisers}
            value={forestMass.appraiserUuid}
            onValueChange={(newAppraiserUuid) => {
              if (newAppraiserUuid) {
                update({appraiserUuid: newAppraiserUuid});
              }
            }}
          />
          {selectedAppraiser && (
            <Stack sx={{pl: 0.5}}>
              <Typography variant="body2" sx={{color: 'grey.700'}}>
                {selectedAppraiser.phoneNumber}
              </Typography>
              <Typography variant="body2" sx={{color: 'grey.700'}}>
                {selectedAppraiser.email}
              </Typography>
            </Stack>
          )}
        </Stack>
      </LabelChip>

      <Divider sx={{m: 2}} variant="fullWidth" />

      <LabelChip value={t('forestMass.species')} icon={ForestOutlinedIcon} direction="row">
        <MultiSelectEditableOnHover
          editable={canEdit}
          selectedOptionsOrientation="column"
          options={speciesOptions ? speciesOptions : []}
          selectedOptions={selectedSpeciesOptions}
          onOptionsChanged={handleSelectedSpeciesChange}
        />
      </LabelChip>
    </>
  );
};
export default ForestMassGeneralInfo;

const speciesToOptions = (species: Array<Species>) => species?.map((s) => ({id: s.uuid, label: s.nameKey}));
